import { Component, Inject, HostListener, ElementRef, ViewChild, OnInit } from '@angular/core';
import { CommonModule, NgIf, NgFor, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../../../environments/environment';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-preview-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    NgIf,
    NgFor,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './preview-dialog.component.html',
  styleUrls: ['./preview-dialog.component.scss']
})
export class PreviewDialogComponent implements OnInit {
  @ViewChild('imageRef') imageRef!: ElementRef<HTMLImageElement>;

  safeImageUrl: SafeResourceUrl | undefined;
  safeOfficeUrl: SafeResourceUrl | undefined;
  safeGoogleUrl: SafeResourceUrl | undefined;

  zoomLevel = 100;
  rotation = 0;
  isDragging = false;
  position = { x: 0, y: 0 };
  dragStart = { x: 0, y: 0 };
  isFullscreen = false;

  fileExt = '';
  isImageFile = false;
  isPdfFile = false;
  isWordFile = false;
  isExcelFile = false;
  isHtml = false;

  viewerMode: 'native' | 'office' | 'google' | 'direct' = 'direct';

  // Excel spreadsheet state
  excelSheets: { name: string; data: any[][] }[] = [];
  selectedSheetIndex = 0;
  loadingDoc = false;
  docError: string | null = null;

  storageUrl = environment.storageUrl;

  constructor(
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: { 
      url: string; 
      title: string; 
      filename?: string; 
      isHtml?: boolean; 
      isPdf?: boolean; 
      isWord?: boolean; 
      isExcel?: boolean;
      attachments?: string[];
      currentIndex?: number;
    }
  ) {}

  ngOnInit(): void {
    this.initDocumentView();
  }

  initDocumentView(): void {
    if (this.data.url && !this.data.url.startsWith('http') && !this.data.url.startsWith('data:') && !this.data.url.startsWith('blob:')) {
      this.data.url = `${environment.storageUrl}/${this.data.url}`;
    }

    if (!this.data.filename) {
      this.data.filename = this.extractFilenameFromUrl(this.data.url);
    }

    this.updateFileDetection(this.data.url);
  }

  updateFileDetection(url: string): void {
    if (!url) return;
    const cleanUrl = url.split('?')[0].split('#')[0];
    this.fileExt = cleanUrl.split('.').pop()?.toLowerCase() || '';

    this.isImageFile = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(this.fileExt);
    this.isPdfFile = this.fileExt === 'pdf' || !!this.data.isPdf;
    this.isWordFile = ['doc', 'docx', 'rtf'].includes(this.fileExt) || !!this.data.isWord;
    this.isExcelFile = ['xls', 'xlsx', 'csv'].includes(this.fileExt) || !!this.data.isExcel;
    this.isHtml = ['html', 'htm'].includes(this.fileExt) || !!this.data.isHtml;

    // Set default viewer mode
    if (this.isExcelFile) {
      this.viewerMode = 'native';
      this.loadExcelData(url);
    } else if (this.isWordFile) {
      this.viewerMode = 'office';
    } else if (this.isPdfFile) {
      this.viewerMode = 'direct';
    } else {
      this.viewerMode = 'direct';
    }

    // Set up safe sanitized URLs for iframes
    this.safeImageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

    const encoded = encodeURIComponent(url);
    this.safeOfficeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://view.officeapps.live.com/op/embed.aspx?src=${encoded}`
    );
    this.safeGoogleUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://docs.google.com/viewer?url=${encoded}&embedded=true`
    );
  }

  loadExcelData(url: string): void {
    if (!this.isExcelFile) return;
    this.loadingDoc = true;
    this.docError = null;
    this.excelSheets = [];

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        return res.arrayBuffer();
      })
      .then(buffer => {
        const workbook = XLSX.read(buffer, { type: 'array' });
        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          throw new Error('No sheets found in workbook');
        }
        this.excelSheets = workbook.SheetNames.map(name => {
          const sheet = workbook.Sheets[name];
          const rawData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
          const trimmed = rawData.slice(0, 250).map(row => (Array.isArray(row) ? row.slice(0, 40) : []));
          return { name, data: trimmed };
        });
        this.selectedSheetIndex = 0;
        this.loadingDoc = false;
      })
      .catch(err => {
        console.warn('Could not parse Excel client-side, falling back to Office/Google iframe viewer:', err);
        this.loadingDoc = false;
        this.viewerMode = 'office';
      });
  }

  showAttachmentAtIndex(index: number): void {
    if (!this.data.attachments || index < 0 || index >= this.data.attachments.length) {
      return;
    }
    this.data.currentIndex = index;
    const url = this.data.attachments[index];

    let displayUrl = url;
    if (displayUrl && !displayUrl.startsWith('http') && !displayUrl.startsWith('data:') && !displayUrl.startsWith('blob:')) {
      displayUrl = `${environment.storageUrl}/${displayUrl}`;
    }
    this.data.url = displayUrl;
    this.data.filename = this.extractFilenameFromUrl(displayUrl);
    this.data.title = this.data.filename;

    this.updateFileDetection(this.data.url);
    this.reset();
  }

  prevAttachment(): void {
    if (this.data.attachments && this.data.currentIndex !== undefined && this.data.currentIndex > 0) {
      this.showAttachmentAtIndex(this.data.currentIndex - 1);
    }
  }

  nextAttachment(): void {
    if (this.data.attachments && this.data.currentIndex !== undefined && this.data.currentIndex < this.data.attachments.length - 1) {
      this.showAttachmentAtIndex(this.data.currentIndex + 1);
    }
  }

  hasMultipleAttachments(): boolean {
    return !!(this.data.attachments && this.data.attachments.length > 1);
  }

  isImage(): boolean {
    return this.isImageFile;
  }

  isPdf(): boolean {
    return this.isPdfFile;
  }

  isWord(): boolean {
    return this.isWordFile;
  }

  isExcel(): boolean {
    return this.isExcelFile;
  }

  getDocTypeLabel(): string {
    if (this.isImageFile) return 'Image';
    if (this.isPdfFile) return 'PDF Document';
    if (this.isWordFile) return 'Word Document';
    if (this.isExcelFile) return 'Excel Spreadsheet';
    if (this.isHtml) return 'HTML Document';
    return (this.fileExt || 'File').toUpperCase();
  }

  getDocTypeIcon(): string {
    if (this.isImageFile) return 'image';
    if (this.isPdfFile) return 'picture_as_pdf';
    if (this.isWordFile) return 'description';
    if (this.isExcelFile) return 'table_view';
    if (this.isHtml) return 'code';
    return 'insert_drive_file';
  }

  private extractFilenameFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.substring(pathname.lastIndexOf('/') + 1) || 'document';
    } catch {
      return 'document';
    }
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + 25, 500);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - 25, 25);
  }

  handleZoomChange(value: number): void {
    this.zoomLevel = value;
  }

  rotate(): void {
    this.rotation = (this.rotation + 90) % 360;
  }

  reset(): void {
    this.zoomLevel = 100;
    this.rotation = 0;
    this.position = { x: 0, y: 0 };
  }

  download(): void {
    if (!this.data.url) return;
    
    fetch(this.data.url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = this.data.filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.error('Error downloading file via blob, falling back to direct link:', error);
        const link = document.createElement('a');
        link.href = this.data.url;
        link.target = '_blank';
        link.download = this.data.filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  handleMouseDown(e: MouseEvent): void {
    if (this.zoomLevel > 100) {
      this.isDragging = true;
      this.dragStart = {
        x: e.clientX - this.position.x,
        y: e.clientY - this.position.y,
      };
    }
  }

  handleMouseMove(e: MouseEvent): void {
    if (this.isDragging && this.zoomLevel > 100) {
      this.position = {
        x: e.clientX - this.dragStart.x,
        y: e.clientY - this.dragStart.y,
      };
    }
  }

  handleMouseUp(): void {
    this.isDragging = false;
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.isFullscreen = false;
  }

  getFileType(filePath: string): 'image' | 'pdf' | 'doc' | 'xls' | 'other' {
    if (!filePath) return 'other';
    const cleanPath = filePath.toLowerCase();
    if (cleanPath.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return 'image';
    } else if (cleanPath.endsWith('.pdf')) {
      return 'pdf';
    } else if (cleanPath.match(/\.(doc|docx)$/i)) {
      return 'doc';
    } else if (cleanPath.match(/\.(xls|xlsx)$/i)) {
      return 'xls';
    }
    return 'other';
  }

  getAttachmentFullUrl(filePath: string): string {
    if (!filePath) return '';
    return filePath.startsWith('http') ? filePath : `${this.storageUrl}/${filePath}`;
  }
}
